"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, ShieldCheck, Clock, PhoneCall, ArrowRight, Sparkles, CircleCheck as CheckCircle2, Droplets } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
const keyHighlights = [{
  icon: ShieldCheck,
  title: "Genuine Moen Parts",
  description: "OEM cartridges, stems, and valve components for lasting performance."
}, {
  icon: Clock,
  title: "24/7 Fast Response",
  description: "Emergency dispatch across Jonesboro and surrounding Clayton County."
}, {
  icon: Wrench,
  title: "Certified Repair Techs",
  description: "Specialized diagnostics for Moen pull-down, touchless, and multi-handle units."
}];
const featurePills = ["Cartridge & Valve Replacement", "Touchless & Smart Faucet Repair", "Kitchen & Bathroom Sink Fixes", "Upfront Pricing & Warranty"];
export default function MoenHero() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="moenhero" data-nav="light" className="relative overflow-hidden bg-background py-12 md:py-20 lg:py-24 border-b border-border">
      {/* Subtle Background Pattern Accent */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-secondary/60 via-background to-background" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Content */}
          <motion.div className="lg:col-span-7 flex flex-col items-start text-left" initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }}>
            {/* Brand Authority Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary border border-border text-secondary-foreground text-xs sm:text-sm font-semibold mb-6 shadow-xs">
              <Sparkles className="size-4 text-accent" />
              <span>Moen Fixture Repair & Installation Specialists</span>
            </div>

            {/* Main Headline */}
            <h1 className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.15] mb-6">
              Expert Moen Faucet &amp; Sink Repair in{" "}
              <span className="text-primary underline decoration-accent decoration-4 underline-offset-4">
                Jonesboro
              </span>
            </h1>

            {/* Clear, Concrete Paragraph */}
            <p className="font-default text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mb-8">
              Keep your kitchen and bathroom running smoothly. Jonesboro Emergency Plumber delivers precision troubleshooting, genuine Moen 1222/1225 cartridge replacements, leak resolution, and flawless fixture installations anytime you need help.
            </p>

            {/* Quick Feature Chips */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full max-w-xl mb-8">
              {featurePills.map((pill, i) => <div key={i} data-index={i} className="flex items-center gap-2 text-xs sm:text-sm text-foreground/90 font-medium bg-secondary/50 rounded-md px-3 py-2 border border-border/60">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{pill}</span>
                </div>)}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="shadow-md font-semibold text-base h-12 px-8">
                <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="size-5" />
                  <span>Request Moen Service</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="font-semibold text-base h-12 px-6">
                <Link to="/service-areas/" className="inline-flex items-center justify-center gap-2">
                  <span>View Service Areas</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>

            {/* Additional Reassurance Text */}
            <div className="mt-6 flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
              <Droplets className="size-4 text-primary shrink-0" />
              <span>Available 24/7 for urgent leak containment and sink emergencies.</span>
            </div>
          </motion.div>

          {/* Right Column: Visual Showcase & Cards */}
          <motion.div className="lg:col-span-5 relative flex flex-col items-center" initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.15,
          ease: "easeOut"
        }}>
            {/* Visual Container */}
            <div className="relative w-full max-w-md lg:max-w-none">
              
              {/* Primary Visual Card */}
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
                <div className="relative h-72 sm:h-80 w-full overflow-hidden bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/moen_faucet_repair_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/moen_faucet_repair_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/moen_faucet_repair_hero.png-wvc-sizes" alt="Moen kitchen faucet and sink installation and repair service in Jonesboro" loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-500 hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  
                  {/* Floating Tag over Image */}
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-accent">Brand Compatibility</p>
                      <p className="text-sm font-bold">Moen Kitchen, Bath & Shower Lines</p>
                    </div>
                    <Badge variant="secondary" className="bg-background/90 text-foreground text-xs backdrop-blur-xs">
                      100% Guaranteed
                    </Badge>
                  </div>
                </div>

                {/* Sub-card details */}
                <CardContent className="p-5 sm:p-6 bg-card">
                  <div className="flex items-center justify-between pb-4 mb-4 border-b border-border">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Standard Dispatch</p>
                      <p className="text-base font-bold text-foreground">Jonesboro &amp; Metro Clayton</p>
                    </div>
                    <Button asChild size="sm" variant="secondary">
                      <Link to="/faucet-sink-repair/">
                        <span>All Fixture Services</span>
                      </Link>
                    </Button>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Experiencing continuous dripping, low pressure, handle stiffness, or temperature balancing issues? Our trucks carry factory-spec Moen replacement parts.
                  </p>
                </CardContent>
              </div>

              {/* Decorative Trust Badge Overlay */}
              <div className="absolute -bottom-5 -left-4 sm:-left-6 bg-primary text-primary-foreground p-3.5 sm:p-4 rounded-xl shadow-lg border border-primary/20 flex items-center gap-3 max-w-[240px]">
                <div className="p-2 rounded-lg bg-primary-foreground/10 shrink-0">
                  <ShieldCheck className="size-6 text-accent" />
                </div>
                <div>
                  <p className="text-xs font-bold leading-tight">Licensed &amp; Insured</p>
                  <p className="text-[11px] text-primary-foreground/80 leading-tight">Jonesboro Local Plumbers</p>
                </div>
              </div>
            </div>

          </motion.div>

        </div>

        {/* Feature Highlights Grid */}
        <div className="mt-16 sm:mt-20 pt-10 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-6">
          {keyHighlights.map((item, i) => {
          const IconComp = item.icon;
          return <Card key={i} data-index={i} className="bg-card border-border/80 shadow-xs hover:shadow-md transition-[box-shadow,border-color] duration-200">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0">
                    <IconComp className="size-5" />
                  </div>
                  <div>
                    <h2 className="font-default text-base font-semibold text-card-foreground mb-1">
                      {item.title}
                    </h2>
                    <p className="font-default text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>

      </div>
    </section>;
}